import {
  Badge,
  BlockStack,
  Button,
  Card,
  Collapsible,
  Icon,
  InlineGrid,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
} from '@shopify/polaris'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  RefreshIcon,
} from '@shopify/polaris-icons'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import useAppState from '../../hooks/useAppState'
import { getLatestJobChain } from '../../apis/jobs'
import styles from './styles.module.css'

// Job types we want to show in the sync card
const SYNC_JOB_TYPES = {
  SYNC_PRODUCTS: 'sync.products',
  SYNC_ORDERS: 'sync.orders',
  GENERATE_PRODUCT_RELATIONSHIPS: 'relationships.generate'  // Fixed job type
}

// Job status constants
const JobStatus = {
  PENDING: 'pending',
  STARTED: 'started',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed'
}

function SyncCard({ initiallyCollapsed = true }) {
  const { t } = useTranslation()
  const { data: appStateData = {} } = useAppState()
  
  // Fetch latest job chain
  const { data: chainData, isLoading } = useQuery({
    queryKey: ['latestChain', 'shop_install'],
    queryFn: () => getLatestJobChain({ type: 'shop_install' }),
    refetchInterval: 15000, // Refresh every 15 seconds
    staleTime: 10000,
  })
  
  // Determine sync status from chain or fallback to app state
  let productSyncStatus, orderSyncStatus, relationshipStatus
  let productSyncRunning, orderSyncRunning, relationshipRunning
  let productSyncPending, orderSyncPending, relationshipPending
  let productProgress, orderProgress, relationshipProgress
  let isDone = false
  let currentStep = ''
  
  if (chainData?.chain && chainData.jobs) {
    // Jobs are already filtered by API (hidden jobs excluded)
    const productJob = chainData.jobs.find(j => j.type === SYNC_JOB_TYPES.SYNC_PRODUCTS)
    const orderJob = chainData.jobs.find(j => j.type === SYNC_JOB_TYPES.SYNC_ORDERS)
    const relationshipJob = chainData.jobs.find(j => j.type === SYNC_JOB_TYPES.GENERATE_PRODUCT_RELATIONSHIPS)
    
    // Check completion status
    productSyncStatus = productJob?.status === JobStatus.COMPLETED
    orderSyncStatus = orderJob?.status === JobStatus.COMPLETED
    relationshipStatus = relationshipJob?.status === JobStatus.COMPLETED
    
    // Check running status
    productSyncRunning = productJob?.status === JobStatus.RUNNING || productJob?.status === JobStatus.STARTED
    orderSyncRunning = orderJob?.status === JobStatus.RUNNING || orderJob?.status === JobStatus.STARTED
    relationshipRunning = relationshipJob?.status === JobStatus.RUNNING || relationshipJob?.status === JobStatus.STARTED
    
    // Check pending status
    productSyncPending = productJob?.status === JobStatus.PENDING
    orderSyncPending = orderJob?.status === JobStatus.PENDING
    relationshipPending = relationshipJob?.status === JobStatus.PENDING
    
    // Get progress percentages
    productProgress = productJob?.progress || 0
    orderProgress = orderJob?.progress || 0
    relationshipProgress = relationshipJob?.progress || 0
    
    // Check if chain is done
    isDone = chainData.chain.status === 'completed' || 
             chainData.chain.status === 'partially_completed' ||
             (productSyncStatus && orderSyncStatus && relationshipStatus)
    
    // Determine current step based on what's running or pending
    if (productJob?.status === JobStatus.RUNNING || productJob?.status === JobStatus.STARTED) {
      currentStep = t('SyncCard.labels.productSync')
    } else if (orderJob?.status === JobStatus.RUNNING || orderJob?.status === JobStatus.STARTED) {
      currentStep = t('SyncCard.labels.orderSync')
    } else if (relationshipJob?.status === JobStatus.RUNNING || relationshipJob?.status === JobStatus.STARTED) {
      currentStep = t('SyncCard.labels.similarProductSync')
    } else if (!productSyncStatus) {
      currentStep = t('SyncCard.labels.productSync')
    } else if (!orderSyncStatus) {
      currentStep = t('SyncCard.labels.orderSync')
    } else if (!relationshipStatus) {
      currentStep = t('SyncCard.labels.similarProductSync')
    }
  } else {
    // Fallback to app state data
    productSyncStatus = appStateData.products?.lastProductSyncedAt
    const widgetSynced = appStateData?.trendingGeneratedAt && appStateData?.bestsellerGeneratedAt
    orderSyncStatus = appStateData.orders?.lastOrderSyncedAt
    relationshipStatus = widgetSynced
    
    // No running or pending status when using app state fallback
    productSyncRunning = false
    orderSyncRunning = false
    relationshipRunning = false
    productSyncPending = false
    orderSyncPending = false
    relationshipPending = false
    productProgress = 0
    orderProgress = 0
    relationshipProgress = 0
    
    isDone = Boolean(productSyncStatus && orderSyncStatus && relationshipStatus)
    
    currentStep = getLabelSequence({ 
      t, 
      sequence: [productSyncStatus, orderSyncStatus, relationshipStatus] 
    })
  }

  return (
    <div
      className="Polaris-Box"
      style={{
        '--pc-box-background': 'var(--p-color-bg-surface)',
        '--pc-box-min-height': '100%',
        '--pc-box-overflow-x': 'clip',
        '--pc-box-overflow-y': 'clip',
        '--pc-box-padding-block-start-xs': 'var(--p-space-400)',
        '--pc-box-padding-block-end-xs': 'var(--p-space-400)',
        '--pc-box-padding-inline-start-xs': 'var(--p-space-400)',
        '--pc-box-padding-inline-end-xs': 'var(--p-space-400)',
        height: '100%',
        border: '1px solid rgb(229, 231, 235)',
        borderRadius: 'var(--p-space-400)',
      }}
    >
      <BlockStack gap={'600'}>
        <BlockStack gap={'100'}>
          <InlineGrid gap={'200'} columns={'auto auto auto 1fr auto'}>
            <Icon source={RefreshIcon} />
            <Text variant="headingMd">{t('SyncCard.title')}</Text>
            <Badge size="small" tone={isDone ? 'success' : 'warning'}>
              {isDone
                ? t('SyncCard.badge.complete')
                : `${t('SyncCard.badge.inProgress')}: ${currentStep}`}
            </Badge>
          </InlineGrid>
          <Text variant="bodyMd">{t('SyncCard.description')}</Text>
        </BlockStack>
        <BlockStack gap={'200'}>
          <BlockStack gap={'100'}>
            <InlineGrid columns={'auto 1fr auto'} gap={'200'}>
              {productSyncStatus ? (
                <Icon source={CheckIcon} />
              ) : productSyncRunning ? (
                <LoadingIcon />
              ) : productSyncPending ? (
                <PendingIcon />
              ) : (
                <PendingIcon />
              )}
              <Text>{t('SyncCard.labels.productSync')}</Text>
              {productSyncRunning && productProgress > 0 && (
                <Text variant="bodySm" tone="subdued">{Math.round(productProgress)}%</Text>
              )}
            </InlineGrid>
            {productSyncRunning && productProgress > 0 && (
              <div style={{ 
                height: '2px', 
                backgroundColor: '#e5e5e5', 
                borderRadius: '1px',
                overflow: 'hidden',
                marginLeft: '26px'
              }}>
                <div style={{ 
                  height: '100%',
                  width: `${productProgress}%`,
                  backgroundColor: '#00a3e0',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            )}
          </BlockStack>
          <BlockStack gap={'100'}>
            <InlineGrid columns={'auto 1fr auto'} gap={'200'}>
              {orderSyncStatus ? (
                <Icon source={CheckIcon} />
              ) : orderSyncRunning ? (
                <LoadingIcon />
              ) : orderSyncPending ? (
                <PendingIcon />
              ) : (
                <PendingIcon />
              )}
              <Text>{t('SyncCard.labels.orderSync')}</Text>
              {orderSyncRunning && orderProgress > 0 && (
                <Text variant="bodySm" tone="subdued">{Math.round(orderProgress)}%</Text>
              )}
            </InlineGrid>
            {orderSyncRunning && orderProgress > 0 && (
              <div style={{ 
                height: '2px', 
                backgroundColor: '#e5e5e5', 
                borderRadius: '1px',
                overflow: 'hidden',
                marginLeft: '26px'
              }}>
                <div style={{ 
                  height: '100%',
                  width: `${orderProgress}%`,
                  backgroundColor: '#00a3e0',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            )}
          </BlockStack>
          <BlockStack gap={'100'}>
            <InlineGrid columns={'auto 1fr auto'} gap={'200'}>
              {relationshipStatus ? (
                <Icon source={CheckIcon} />
              ) : relationshipRunning ? (
                <LoadingIcon />
              ) : relationshipPending ? (
                <PendingIcon />
              ) : (
                <PendingIcon />
              )}
              <Text>{t('SyncCard.labels.similarProductSync')}</Text>
              {relationshipRunning && relationshipProgress > 0 && (
                <Text variant="bodySm" tone="subdued">{Math.round(relationshipProgress)}%</Text>
              )}
            </InlineGrid>
            {relationshipRunning && relationshipProgress > 0 && (
              <div style={{ 
                height: '2px', 
                backgroundColor: '#e5e5e5', 
                borderRadius: '1px',
                overflow: 'hidden',
                marginLeft: '26px'
              }}>
                <div style={{ 
                  height: '100%',
                  width: `${relationshipProgress}%`,
                  backgroundColor: '#00a3e0',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            )}
          </BlockStack>
        </BlockStack>
        
        {/* Show progress bar if chain is active */}
        {chainData?.chain && !isDone && (() => {
          // Jobs are already filtered by API (hidden jobs excluded)
          const visibleCompleted = chainData.jobs.filter(j => j.status === 'completed').length
          const visibleTotal = chainData.jobs.length
          const progressPercent = visibleTotal > 0 ? (visibleCompleted / visibleTotal) * 100 : 0
          
          return (
            <BlockStack gap={'200'}>
              <div style={{ 
                height: '4px', 
                backgroundColor: '#e5e5e5', 
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  height: '100%',
                  width: `${progressPercent}%`,
                  backgroundColor: '#00a3e0',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <Text variant="bodySm" tone="subdued">
                {t('SyncCard.progress', {
                  completed: visibleCompleted,
                  total: visibleTotal
                })}
              </Text>
            </BlockStack>
          )
        })()}
      </BlockStack>
    </div>
  )
}

export default SyncCard

const LoadingIcon = () => (
  <svg
    className={styles.spin}
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <circle
      cx="9"
      cy="9"
      r="8.25"
      stroke="#898A8A"
      strokeWidth="1.5"
      strokeDasharray="3 3"
    />
  </svg>
)

const PendingIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
  >
    <circle
      cx="9"
      cy="9"
      r="8.25"
      stroke="#C0C0C0"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
)

function getLabelSequence({ t, sequence = [] }) {
  if (!sequence[0]) return t('SyncCard.labels.productSync')
  if (!sequence[1]) return t('SyncCard.labels.orderSync')
  return t('SyncCard.labels.similarProductSync')
}